import { SlideFrame } from '../SlideFrame';
import type { ModelStats } from '../types';

// Sub-sub-factor structure: each pillar's factor list mixes universal factors
// with peer-group-specialized ones tagged by sector. We don't apply the same
// lens to every stock — a bank doesn't have gross margin; a REIT's GAAP
// earnings misunderstand its economics. Tags match `lib/peer-groups.ts`.
type FactorEntry = { f: string; tag?: 'BANKS' | 'REIT' | 'UTIL' };
const PILLARS: Array<{ key: 'profitability' | 'growth' | 'safety' | 'shareholderReturns'; name: string; sample: FactorEntry[] }> = [
  { key: 'profitability', name: 'Profitability', sample: [
    { f: 'Forward ROE' }, { f: 'Forward ROA' }, { f: 'Forward Net Margin' },
    { f: 'ROIC' }, { f: 'Gross Profit / Assets' }, { f: 'Accruals Ratio' }, { f: 'ROE' },
    { f: 'ROTCE', tag: 'BANKS' }, { f: 'Net Interest Margin', tag: 'BANKS' },
    { f: 'Efficiency Ratio', tag: 'BANKS' }, { f: 'Provisions / NII', tag: 'BANKS' },
    { f: 'FFO Yield', tag: 'REIT' }, { f: 'Occupancy Rate', tag: 'REIT' },
    { f: 'Same-Store NOI Growth', tag: 'REIT' },
  ]},
  { key: 'growth', name: 'Growth', sample: [
    { f: 'Forward EPS CAGR (2y)' }, { f: 'Forward EPS CAGR (3y)' }, { f: 'Forward Revenue CAGR' },
    { f: 'Next-Quarter EPS' }, { f: 'Operating Leverage' }, { f: 'FCF Growth (3y)' }, { f: 'Revenue Growth (3y)' },
    { f: 'R&D Intensity' },
  ]},
  { key: 'safety', name: 'Safety', sample: [
    { f: 'Forward Debt / EBITDA' }, { f: 'Current Ratio' }, { f: 'Debt / EBITDA' },
    { f: 'Beta (low)' }, { f: 'Equity / Assets' },
    { f: 'CDS Spread' }, { f: 'Short Interest (days)' },
    { f: 'Tier-1 Capital Ratio', tag: 'BANKS' }, { f: 'Non-Performing Loan Ratio', tag: 'BANKS' },
    { f: 'Provision Coverage', tag: 'BANKS' },
    { f: 'Avg Lease Term', tag: 'REIT' },
  ]},
  { key: 'shareholderReturns', name: 'Payout', sample: [
    { f: 'Forward Dividend Yield' }, { f: 'Implied Buyback Yield' }, { f: 'Buyback Yield' },
    { f: 'Net Debt Paydown' }, { f: 'Insider Net Buy' },
    { f: 'SBC Dilution (inv)' },
  ]},
];

const TAG_COLOR: Record<NonNullable<FactorEntry['tag']>, string> = {
  BANKS: '#60a5fa',
  REIT: '#22c55e',
  UTIL: '#10b981',
};

// Valuation is 14 lenses total: 11 peer-relative multiples weighted 75% and
// 3 universe-scaled absolute lenses weighted 25%. Splitting them visually
// makes the structure of the V axis legible — peer-relative tells you cheap
// vs. peers, absolute tells you cheap vs. fundamentals.
const PEER_MULTIPLES: FactorEntry[] = [
  { f: 'Forward P/E (1-year)' }, { f: 'Forward P/E (2-year)' }, { f: 'Forward P/E (3-year)' },
  { f: 'Forward EV/EBITDA' }, { f: 'Forward P/Book' }, { f: 'PEG Ratio' },
  { f: 'EV/EBITDA (trailing)' }, { f: 'P/Book (trailing)' }, { f: 'P/Sales' },
  { f: 'P/FFO', tag: 'REIT' }, { f: 'P/NAV', tag: 'REIT' },
];

const ABSOLUTE_LENSES = [
  { name: 'Asymmetry', desc: 'Upside-to-downside ratio anchored on own 5Y P/E history' },
  { name: 'DCF Discount', desc: 'Discount to owner-earnings discounted cash flow' },
  { name: 'Fair-Value Discount', desc: "Discount to today's implied fair value from forward earnings" },
];

export function Slide07FactorModel({ stats }: { stats: ModelStats | null }) {
  return (
    <SlideFrame pageNum={10} title="THE VISUALIZER  —  HOW WE LOOK AT A COMPANY" titleOffsetPx={19}
      banner="40 quality factors, 14 valuation factors, six peer groups — one composite score per company, every day.">
      <div className="h-full flex flex-col justify-center gap-3 pt-1 pb-8">

        {/* QUALITY — 4 pillars */}
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="flex items-baseline gap-3">
              <span className="text-[#c9a84c] text-[14px] font-bold tracking-[0.2em]">QUALITY</span>
              <span className="text-white text-[16px] font-bold tabular-nums">{stats?.qualityFactorCount ?? 27} factors</span>
            </div>
            <div className="text-[#94a3b8] text-[12px]">
              across 4 pillars{stats?.quality?.forwardPct != null && (
                <> · {Math.round(stats.quality.forwardPct * 100)}% forward-looking</>
              )}
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2.5">
            {PILLARS.map(p => {
              const fwd = stats?.pillars?.[p.key]?.forwardPct;
              return (
                <div key={p.key} className="bg-[#1e293b] rounded-xl p-2 flex flex-col">
                  <div className="text-white text-[13px] font-bold leading-tight">{p.name}</div>
                  {fwd != null && (
                    <div className="text-[#4ade80] text-[9px] mb-1 tabular-nums">{Math.round(fwd * 100)}% forward</div>
                  )}
                  <ul className={`text-[#cbd5e1] text-[10px] space-y-0 leading-tight mt-0.5 ${p.sample.length > 7 ? 'columns-2 gap-x-2' : ''}`}>
                    {p.sample.map(f => (
                      <li key={f.f} className="break-inside-avoid">
                        · {f.f}
                        {f.tag && (
                          <span
                            className="ml-1 text-[7px] font-bold tracking-wider align-middle"
                            style={{ color: TAG_COLOR[f.tag] }}
                          >
                            {f.tag}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        {/* VALUATION — 11 peer + 3 absolute, visually split */}
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="flex items-baseline gap-3">
              <span className="text-[#c9a84c] text-[14px] font-bold tracking-[0.2em]">VALUATION</span>
              <span className="text-white text-[16px] font-bold tabular-nums">14 factors</span>
            </div>
            <div className="text-[#94a3b8] text-[12px]">
              11 peer-relative (75%) + 3 absolute (25%){stats?.valuation?.forwardPct != null && (
                <> · {Math.round(stats.valuation.forwardPct * 100)}% forward-looking</>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            {/* Left column: 11 peer-relative multiples */}
            <div className="bg-[#1e293b] rounded-xl p-2 col-span-2">
              <div className="text-white text-[12px] font-bold mb-1">11 peer-relative multiples</div>
              <div className="text-[#94a3b8] text-[9px] leading-tight mb-1.5">Cheap or expensive vs. industry peers, sector-z scored</div>
              <div className="grid grid-cols-3 gap-x-3 gap-y-0">
                {PEER_MULTIPLES.map(f => (
                  <div key={f.f} className="text-[#cbd5e1] text-[10px] leading-tight">
                    · {f.f}
                    {f.tag && (
                      <span
                        className="ml-1 text-[7px] font-bold tracking-wider align-middle"
                        style={{ color: TAG_COLOR[f.tag] }}
                      >
                        {f.tag}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {/* Right column: 3 absolute factors */}
            <div className="bg-[#1e293b] rounded-xl p-2">
              <div className="text-white text-[12px] font-bold mb-1">3 absolute factors</div>
              <div className="text-[#94a3b8] text-[9px] leading-tight mb-1.5">Cheap or expensive vs. own fundamentals</div>
              <div className="space-y-1">
                {ABSOLUTE_LENSES.map(l => (
                  <div key={l.name}>
                    <div className="text-[#cbd5e1] text-[10px] leading-tight font-semibold">· {l.name}</div>
                    <div className="text-[#64748b] text-[9px] leading-tight ml-2">{l.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* COMPOSITE — show the asymmetric mechanism */}
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <div className="text-[#c9a84c] text-[14px] font-bold tracking-[0.2em]">COMPOSITE SCORE</div>
            <div className="text-[#cbd5e1] text-[11px] italic">Asymmetric by design</div>
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            <div className="bg-[#0f1623] border border-emerald-500/40 rounded-xl p-2.5">
              <div className="text-emerald-300 text-[10px] uppercase tracking-wider font-bold mb-1">When V is favourable</div>
              <div className="text-white text-[15px] font-mono mb-1">√(Q² + V²)</div>
              <div className="text-[#cbd5e1] text-[10px] leading-tight">Quality and cheapness compound geometrically. A great company at a reasonable price is rewarded quadratically.</div>
            </div>
            <div className="bg-[#0f1623] border border-red-500/40 rounded-xl p-2.5">
              <div className="text-red-300 text-[10px] uppercase tracking-wider font-bold mb-1">When V is unfavourable</div>
              <div className="text-white text-[15px] font-mono mb-1">Q + V</div>
              <div className="text-[#cbd5e1] text-[10px] leading-tight">Penalised linearly. A great company at a bad price never dominates. Overvaluation costs you, one-for-one.</div>
            </div>
            <div className="bg-[#c9a84c]/10 border border-[#c9a84c]/50 rounded-xl p-2.5">
              <div className="text-[#c9a84c] text-[10px] uppercase tracking-wider font-bold mb-1">The result</div>
              <div className="text-white text-[15px] font-bold mb-1">One number, daily</div>
              <div className="text-[#cbd5e1] text-[10px] leading-tight">Smooth, monotonic, range −6 to +7. The Alpha Zone is the top 10 by composite, gated to positive quality only.</div>
            </div>
          </div>
        </div>

      </div>
    </SlideFrame>
  );
}
